import { orderConfigToAwsJsonForGql } from '../components/editors/shared/data-tab/chart-data-order.util';
import { sanitizeCardDataConfigForGql } from './card-data-gql.util';
import type {
	CardItemPatchInput,
	CardDataInput,
	CardStyleInput,
} from '../graphql/mutations/update-card-with-version';
import type { CardConfig } from '../models/card-model';
import type { ChartCardConfig } from '../models/chart-config.model';
import { cardStyleColorsClearGqlInput } from './card-style-clear-patch.util';
import { persistableImageRef } from './card-image-ref.util';

/**
 * Maps client patch values to GraphQL input values.
 * Preserves explicit `null` to signal "clear this field" to the backend.
 * Only omits the field when the value is `undefined` (meaning "don't change").
 */
function patchScalarStringForGql(
	value: string | null | undefined
): string | null | undefined {
	if (value === undefined) return undefined;
	// Preserve explicit null to signal "clear this field" to backend
	// Backend should interpret null as "set to empty/clear", not "no change"
	return value;
}

/**
 * AppSync `AWSJSON` variables must be valid JSON strings (see `buildCardPatch` in reports).
 */
function patchAwsJsonForGql(value: unknown): string | undefined {
	if (value === undefined) return undefined;
	if (value === null) return '{}';
	if (typeof value === 'string') {
		const trimmed = value.trim();
		return trimmed.length === 0 ? '{}' : value;
	}
	try {
		return JSON.stringify(value);
	} catch {
		return undefined;
	}
}

/** Sanitizes card `data` for AppSync `CardDataConfigInput` (updateCard / report patch). */
export function cardDataPatchForGql(value: unknown): CardDataInput | undefined {
	return patchDataForGql(value);
}

function patchDataForGql(value: unknown): CardDataInput | undefined {
	if (value === undefined) return undefined;
	if (value === null) return {};
	if (typeof value !== 'object' || Array.isArray(value)) {
		return value as CardDataInput;
	}

	const record = sanitizeCardDataConfigForGql(value as Record<string, unknown>);
	const order = orderConfigToAwsJsonForGql(record.order);
	if (order) {
		record.order = order;
	} else {
		delete record.order;
	}
	if (Object.keys(record).length === 0) {
		return undefined;
	}
	return record as CardDataInput;
}

function patchStyleForGql(
	value: CardConfig['style'] | null | undefined
): CardStyleInput | undefined {
	if (value === undefined) return undefined;
	if (value === null) {
		return { colors: cardStyleColorsClearGqlInput() };
	}
	return value as unknown as CardStyleInput;
}

/**
 * Converts any {@link CardConfig} patch to {@link CardItemPatchInput} for `updateCard`.
 * Preserves `null` for string fields to signal "clear this field" to the backend.
 * Chart-only keys (`palette`, `visualization`, `presentation`, `chartType`) are read when present on the patch.
 */
export function cardPatchToGqlInput(patch: Partial<CardConfig>): CardItemPatchInput {
	const input: CardItemPatchInput = {};
	const chartPatch = patch as Partial<ChartCardConfig>;
	/** `data` exists only on some card variants; avoid union narrowing errors. */
	const patchRecord = patch as Partial<CardConfig> & Record<string, unknown>;

	if (patch.title !== undefined) {
		input.title = patchScalarStringForGql(patch.title);
	}
	if (patch.subtitle !== undefined) {
		input.subtitle = patchScalarStringForGql(patch.subtitle);
	}
	if (patch.content !== undefined) {
		input.content = patchScalarStringForGql(patch.content);
	}
	if (patch.clientId !== undefined) {
		input.clientId = patchScalarStringForGql(patch.clientId);
	}
	if (patch.pageId !== undefined) {
		input.pageId = patchScalarStringForGql(patch.pageId);
	}
	if (patch.cardType !== undefined) {
		input.cardType = patchScalarStringForGql(patch.cardType as string);
	}
	if (chartPatch.chartType !== undefined) {
		input.chartType = patchScalarStringForGql(chartPatch.chartType as string);
	}

	if (chartPatch.palette !== undefined) {
		if (chartPatch.palette === null) {
			input.paletteId = null;
		} else {
			const raw = (
				chartPatch.palette as { selectedPaletteId?: string | null }
			)?.selectedPaletteId;
			if (raw !== undefined) {
				input.paletteId = raw;
			}
		}
	}

	if (patchRecord.data !== undefined) {
		input.data = patchDataForGql(patchRecord.data);
	}
	if (chartPatch.visualization !== undefined) {
		input.visualization = patchAwsJsonForGql(chartPatch.visualization);
	}
	if (chartPatch.presentation !== undefined) {
		input.presentation = patchAwsJsonForGql(chartPatch.presentation);
	}
	if (patch.style !== undefined) {
		input.style = patchStyleForGql(patch.style);
	}
	if (patch.icon !== undefined) {
		input.icon = patchScalarStringForGql(patch.icon);
	}
	if (patch.image !== undefined) {
		const image = patchScalarStringForGql(patch.image);
		input.image =
			image === null || image === undefined
				? image
				: persistableImageRef(image) ?? undefined;
	}
	const imageRef = (patch as { imageRef?: string | null }).imageRef;
	if (imageRef !== undefined && input.image === undefined) {
		if (imageRef === null) {
			input.image = null;
		} else {
			const persistable = persistableImageRef(imageRef);
			if (persistable !== undefined) {
				input.image = persistable;
			}
		}
	}
	if (patch.filterIds !== undefined) {
		input.filterIds = patch.filterIds;
	}

	return input;
}
